import { useState, useEffect, useRef, useCallback } from 'react';
import { X, Loader2, Upload, Truck, FileText, Trash2, Plus, MapPin, Search } from 'lucide-react';
import { cn, API_BASE_URL } from '../../lib/utils';
import { geocodeUkPostcode, normalizeUkPostcode } from '../../lib/ukPostcodeGeocode';
import { useAppSelector } from '../../store';
import { useSystemConfig } from '../../contexts/SystemConfigContext';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw';
import 'leaflet-draw/dist/leaflet.draw.css';
import 'leaflet-imageoverlay-rotated';
import type { C2Event, EventCategory } from '../../types';
import { saveEventMapDrawingsApi } from '../../lib/mapDrawings';
import type { EventMapDrawing } from '../../lib/mapDrawings';
import MapOverlayControls from '../map/MapOverlayControls';
import {
  createOverlayLayer,
  updateOverlayLayer,
  saveEventMapOverlayApi,
  type EventMapOverlaySavePayload,
  type RotatedOverlayLayer,
} from '../../lib/mapOverlay';

type EventResource = { id: string; name: string; type: string; department: string };

type TabId = 'basics' | 'resources' | 'map' | 'uploads';

const tabs: { id: TabId; label: string }[] = [
  { id: 'map', label: 'Map' },
  { id: 'basics', label: 'Event Basics' },
  { id: 'resources', label: 'Resources' },
  { id: 'uploads', label: 'Uploads' },
];

interface CreateEventModalProps {
  open: boolean;
  onClose: () => void;
  onCreated?: (event: C2Event) => void;
}

export default function CreateEventModal({ open, onClose, onCreated }: CreateEventModalProps) {
  const { token } = useAppSelector((state) => state.auth);
  const { enabledEventCategories, enabledResourceTypes } = useSystemConfig();
  const categoryOptions = enabledEventCategories.map((c) => c.id as EventCategory);
  const resourceTypes = enabledResourceTypes.map((r) => r.id);
  const [mode, setMode] = useState<'detail' | 'quick'>('detail');
  const [activeTab, setActiveTab] = useState<TabId>('map');
  const [creating, setCreating] = useState(false);

  // Event basics
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<EventCategory>('other');
  const [eventDate, setEventDate] = useState(new Date().toISOString().split('T')[0]);
  const [eventStartTime, setEventStartTime] = useState(
    new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
  );
  const [address, setAddress] = useState('');
  const [postcodeSearch, setPostcodeSearch] = useState('');
  const [postcodeSearching, setPostcodeSearching] = useState(false);
  const [postcodeError, setPostcodeError] = useState<string | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [pickingLocation, setPickingLocation] = useState(false);
  const [mapOverlayDraft, setMapOverlayDraft] = useState<EventMapOverlaySavePayload | null>(null);
  const locationMarkerRef = useRef<L.Marker | null>(null);
  const overlayLayerRef = useRef<RotatedOverlayLayer | null>(null);
  const pickingLocationRef = useRef(false);
  pickingLocationRef.current = pickingLocation;

  // Resources (created per-event)
  const [eventResources, setEventResources] = useState<EventResource[]>([]);
  const [newResName, setNewResName] = useState('');
  const [newResType, setNewResType] = useState('vehicle');
  const [newResDept, setNewResDept] = useState('');

  // Uploads (mock - files stored as names only in this demo)
  const [uploads, setUploads] = useState<{ id: string; name: string; size: number }[]>([]);

  // Map ref
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  const tileLayerRef = useRef<L.TileLayer | null>(null);
  const drawnItemsRef = useRef<L.FeatureGroup>(new L.FeatureGroup());
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const resizeDebounceRef = useRef<number | null>(null);

  const refreshMapSize = () => {
    if (resizeDebounceRef.current) {
      window.clearTimeout(resizeDebounceRef.current);
    }
    resizeDebounceRef.current = window.setTimeout(() => {
      const map = mapInstanceRef.current;
      const container = mapContainerRef.current;
      if (!map || !container) return;

      const { width, height } = container.getBoundingClientRect();
      if (width < 10 || height < 10) return;

      map.invalidateSize({ animate: false });
      tileLayerRef.current?.redraw();
    }, 100);
  };

  // Reset form map state when modal opens; destroy map when it closes
  useEffect(() => {
    if (open) {
      drawnItemsRef.current.clearLayers();
      setSelectedLocation(null);
      setPickingLocation(false);
      setMapOverlayDraft(null);
      if (locationMarkerRef.current && mapInstanceRef.current) {
        mapInstanceRef.current.removeLayer(locationMarkerRef.current);
        locationMarkerRef.current = null;
      }
      return;
    }

    resizeObserverRef.current?.disconnect();
    resizeObserverRef.current = null;
    overlayLayerRef.current = null;
    tileLayerRef.current = null;
    if (resizeDebounceRef.current) {
      window.clearTimeout(resizeDebounceRef.current);
      resizeDebounceRef.current = null;
    }
    if (mapInstanceRef.current) {
      try {
        mapInstanceRef.current.remove();
      } catch {
        // already disposed
      }
      mapInstanceRef.current = null;
      locationMarkerRef.current = null;
    }
  }, [open]);

  const placeEventLocationMarker = (map: L.Map, lat: number, lng: number) => {
    if (locationMarkerRef.current) {
      map.removeLayer(locationMarkerRef.current);
    }
    const marker = L.marker([lat, lng], {
      icon: L.divIcon({
        className: 'custom-location-marker',
        html: `<div style="width:22px;height:22px;background:#ef4444;border:3px solid #fff;border-radius:50%;box-shadow:0 0 12px rgba(239,68,68,0.55);"></div>`,
        iconSize: [22, 22],
        iconAnchor: [11, 11],
      }),
    }).addTo(map);
    marker.bindTooltip('Event location', { permanent: false, direction: 'top' });
    locationMarkerRef.current = marker;
  };

  const goToEventLocation = useCallback((lat: number, lng: number, label?: string) => {
    setSelectedLocation({ lat, lng });
    setPickingLocation(false);
    setPostcodeError(null);
    if (label) setAddress(label);

    const map = mapInstanceRef.current;
    if (map) {
      placeEventLocationMarker(map, lat, lng);
      map.setView([lat, lng], 16, { animate: true });
      refreshMapSize();
    }
  }, []);

  const handlePostcodeSearch = useCallback(async () => {
    const query = postcodeSearch.trim();
    if (!query) {
      setPostcodeError('Enter a UK postcode');
      return;
    }

    setPostcodeSearching(true);
    setPostcodeError(null);
    try {
      const result = await geocodeUkPostcode(query);
      if (!result) {
        setPostcodeError(`Postcode not found — check "${normalizeUkPostcode(query)}"`);
        return;
      }
      setPostcodeSearch(result.postcode);
      goToEventLocation(result.lat, result.lng, result.address);
    } catch {
      setPostcodeError('Postcode lookup failed — try again');
    } finally {
      setPostcodeSearching(false);
    }
  }, [postcodeSearch, goToEventLocation]);

  // Initialize map when the map tab is visible and the container has dimensions
  useEffect(() => {
    if (!open || mode !== 'detail' || activeTab !== 'map') return;
    if (!mapContainerRef.current || mapInstanceRef.current) return;

    const container = mapContainerRef.current;
    const { width, height } = container.getBoundingClientRect();
    if (width < 10 || height < 10) return;

    const initialCenter: [number, number] = [51.505, -0.09];

    const map = L.map(container, {
      center: initialCenter,
      zoom: 13,
      zoomControl: true,
      preferCanvas: false,
    });

    tileLayerRef.current = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap',
      maxZoom: 19,
    });
    tileLayerRef.current.addTo(map);

    drawnItemsRef.current.addTo(map);
    mapInstanceRef.current = map;

    const drawControl = new L.Control.Draw({
      position: 'topright',
      draw: {
        polygon: { shapeOptions: { color: '#3b82f6', fillOpacity: 0.1, weight: 2 } },
        polyline: { shapeOptions: { color: '#3b82f6', weight: 2 } },
        rectangle: { shapeOptions: { color: '#3b82f6', fillOpacity: 0.1, weight: 2 } },
        circle: { shapeOptions: { color: '#3b82f6', fillOpacity: 0.1, weight: 2 } },
        marker: true,
        circlemarker: false,
      } as L.Control.DrawOptions['draw'],
      edit: { featureGroup: drawnItemsRef.current },
    });
    map.addControl(drawControl);

    map.on(L.Draw.Event.CREATED, (e: L.LeafletEvent & { layer: L.Layer }) => {
      drawnItemsRef.current.addLayer(e.layer);
    });

    map.on(L.Draw.Event.DRAWSTART, () => {
      setPickingLocation(false);
    });

    const handleMapClick = (e: L.LeafletMouseEvent) => {
      if (!pickingLocationRef.current) return;
      const { lat, lng } = e.latlng;
      setSelectedLocation({ lat, lng });
      placeEventLocationMarker(map, lat, lng);
      setPickingLocation(false);
      refreshMapSize();
    };

    map.on('click', handleMapClick);

    resizeObserverRef.current = new ResizeObserver(() => refreshMapSize());
    resizeObserverRef.current.observe(container);

    refreshMapSize();
    const t1 = window.setTimeout(refreshMapSize, 150);
    const t2 = window.setTimeout(refreshMapSize, 500);

    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      resizeObserverRef.current?.disconnect();
      resizeObserverRef.current = null;
      map.off('click', handleMapClick);
      overlayLayerRef.current = null;
      tileLayerRef.current = null;
      if (mapInstanceRef.current === map) {
        try {
          map.remove();
        } catch {
          // already disposed
        }
        mapInstanceRef.current = null;
        locationMarkerRef.current = null;
      }
    };
  }, [open, mode, activeTab]);

  // Pin-mode UI changes layout — refresh tiles so the base map stays visible
  useEffect(() => {
    if (!open || mode !== 'detail' || activeTab !== 'map') return;
    refreshMapSize();
  }, [pickingLocation, open, mode, activeTab]);

  // Leaflet needs a size refresh when the map tab becomes visible
  useEffect(() => {
    if (!open || mode !== 'detail' || activeTab !== 'map') return;
    refreshMapSize();
    const t1 = window.setTimeout(refreshMapSize, 100);
    const t2 = window.setTimeout(refreshMapSize, 500);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [open, mode, activeTab]);

  // Keep the event pin marker in sync when location changes externally
  useEffect(() => {
    if (!selectedLocation || !mapInstanceRef.current) return;
    placeEventLocationMarker(
      mapInstanceRef.current,
      selectedLocation.lat,
      selectedLocation.lng
    );
  }, [selectedLocation?.lat, selectedLocation?.lng]);

  // Preview uploaded site map overlay on the create-event map
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;

    if (!mapOverlayDraft?.imageDataUrl) {
      if (overlayLayerRef.current) {
        map.removeLayer(overlayLayerRef.current);
        overlayLayerRef.current = null;
      }
      return;
    }

    const config = {
      ...mapOverlayDraft,
      imageUrl: mapOverlayDraft.imageDataUrl,
    };

    if (overlayLayerRef.current) {
      updateOverlayLayer(overlayLayerRef.current, config);
    } else {
      overlayLayerRef.current = createOverlayLayer(map, config);
    }
  }, [mapOverlayDraft]);

  const reset = () => {
    setTitle('');
    setDescription('');
    setCategory('other');
    setEventDate(new Date().toISOString().split('T')[0]);
    setEventStartTime(new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }));
    setAddress('');
    setPostcodeSearch('');
    setPostcodeError(null);
    setSelectedLocation(null);
    setPickingLocation(false);
    setMapOverlayDraft(null);
    setEventResources([]);
    setNewResName('');
    setNewResType('vehicle');
    setNewResDept('');
    setUploads([]);
    setActiveTab('map');
    setMode('detail');
    drawnItemsRef.current.clearLayers();
    if (locationMarkerRef.current) {
      locationMarkerRef.current = null;
    }
    if (mapInstanceRef.current) {
      try {
        mapInstanceRef.current.remove();
      } catch {
        // already disposed
      }
      mapInstanceRef.current = null;
    }
  };

  const handleCreate = async () => {
    if (!title.trim() || !selectedLocation) return;
    setCreating(true);
    try {
      // Create the event first
      const res = await fetch(`${API_BASE_URL}/events`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          name: title,
          description,
          category,
          eventDate,
          eventStartTime,
          location: {
            latitude: selectedLocation.lat,
            longitude: selectedLocation.lng,
            address: address.trim() || 'Selected on map',
          },
        }),
      });

      if (res.ok) {
        const data = await res.json();
        const createdEvent = data.event;

        // Save map drawings to the server for this event (shared across all users)
        const layers = drawnItemsRef.current.getLayers();
        if (layers.length > 0 && token) {
          const serializedDrawings: EventMapDrawing[] = layers
            .map((layer: L.Layer) => {
              const id = `draw-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

              if (layer instanceof L.Marker) {
                const ll = layer.getLatLng();
                return {
                  id,
                  type: 'marker' as const,
                  latlng: { lat: ll.lat, lng: ll.lng },
                  locked: true,
                };
              }
              if (layer instanceof L.Circle) {
                const ll = layer.getLatLng();
                return {
                  id,
                  type: 'circle' as const,
                  latlng: { lat: ll.lat, lng: ll.lng },
                  radius: layer.getRadius(),
                  locked: true,
                };
              }
              if (layer instanceof L.Polyline) {
                const latlngs = (layer.getLatLngs() as L.LatLng[]).map((ll) => ({
                  lat: ll.lat,
                  lng: ll.lng,
                }));
                return {
                  id,
                  type: 'polyline' as const,
                  latlngs,
                  locked: true,
                };
              }
              if (layer instanceof L.Polygon) {
                const rings = layer.getLatLngs() as L.LatLng[][];
                return {
                  id,
                  type: 'polygon' as const,
                  latlngs: rings.map((ring) =>
                    ring.map((ll) => ({ lat: ll.lat, lng: ll.lng }))
                  ),
                  locked: true,
                };
              }
              return null;
            })
            .filter((d): d is EventMapDrawing => d !== null);

          if (serializedDrawings.length > 0) {
            await saveEventMapDrawingsApi(createdEvent.id, token, serializedDrawings);
          }
        }

        if (mapOverlayDraft?.imageDataUrl && token) {
          await saveEventMapOverlayApi(createdEvent.id, token, {
            ...mapOverlayDraft,
            centerLat: mapOverlayDraft.centerLat || selectedLocation.lat,
            centerLng: mapOverlayDraft.centerLng || selectedLocation.lng,
          });
        }

        // Create resources for this event
        if (eventResources.length > 0) {
          await Promise.all(
            eventResources.map(resource =>
              fetch(`${API_BASE_URL}/resources`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({
                  eventId: createdEvent.id,
                  name: resource.name,
                  type: resource.type,
                  department: resource.department,
                }),
              })
            )
          );
        }

        onCreated?.(createdEvent);
        reset();
        onClose();
      }
    } catch { /* ignore */ } finally {
      setCreating(false);
    }
  };

  const handleQuickCreate = async () => {
    if (!title.trim()) return;
    setCreating(true);
    try {
      const res = await fetch(`${API_BASE_URL}/events`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          name: title,
          description: description || title,
          category,
          eventDate,
          eventStartTime,
          location: { latitude: 0, longitude: 0, address: address || 'TBD' },
        }),
      });
      if (res.ok) {
        const data = await res.json();
        onCreated?.(data.event);
        reset();
        onClose();
      }
    } catch { /* ignore */ } finally {
      setCreating(false);
    }
  };

  const addResource = () => {
    if (!newResName.trim()) return;
    setEventResources((prev) => [
      ...prev,
      { id: `res-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, name: newResName.trim(), type: newResType, department: newResDept.trim() || 'General' },
    ]);
    setNewResName('');
    setNewResDept('');
  };

  const removeResource = (id: string) => {
    setEventResources((prev) => prev.filter((r) => r.id !== id));
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    const newUploads = Array.from(files).map((f) => ({
      id: `upload-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      name: f.name,
      size: f.size,
    }));
    setUploads((prev) => [...prev, ...newUploads]);
    e.target.value = '';
  };

  const removeUpload = (id: string) => {
    setUploads((prev) => prev.filter((u) => u.id !== id));
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };


  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="w-full max-w-3xl bg-[#0f1526] border border-white/[0.08] rounded-2xl shadow-2xl flex flex-col max-h-[85vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/[0.06] flex-shrink-0">
          <div>
            <h2 className="text-base font-bold text-white">Create event</h2>
            <p className="text-xs text-white/30 mt-0.5">Set up a new operational event with resources, maps, and documents.</p>
          </div>
          <div className="flex items-center gap-3">
            {/* Mode toggle */}
            <div className="flex items-center gap-1 bg-white/[0.04] rounded-lg p-0.5">
              <button
                onClick={() => setMode('detail')}
                className={cn(
                  'px-3 py-1.5 rounded-md text-xs font-semibold transition-all',
                  mode === 'detail'
                    ? 'bg-primary text-primary-foreground'
                    : 'text-white/40 hover:text-white/60'
                )}
              >
                Detail Create
              </button>
              <button
                onClick={() => setMode('quick')}
                className={cn(
                  'px-3 py-1.5 rounded-md text-xs font-semibold transition-all',
                  mode === 'quick'
                    ? 'bg-primary text-primary-foreground'
                    : 'text-white/40 hover:text-white/60'
                )}
              >
                Quick Create
              </button>
            </div>
            <button onClick={() => { reset(); onClose(); }} className="p-1.5 rounded-lg hover:bg-white/[0.06] transition-colors">
              <X className="w-4 h-4 text-white/40" />
            </button>
          </div>
        </div>

        {/* Tabs (Detail mode only) */}
        {mode === 'detail' && (
          <div className="flex gap-1 px-6 pt-3 border-b border-white/[0.06] flex-shrink-0">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  'px-4 py-2 text-xs font-semibold rounded-t-lg transition-all relative',
                  activeTab === tab.id
                    ? 'text-primary bg-white/[0.04]'
                    : 'text-white/40 hover:text-white/60'
                )}
              >
                {tab.label}
                {activeTab === tab.id && (
                  <div className="absolute bottom-0 left-2 right-2 h-[2px] bg-primary rounded-full" />
                )}
              </button>
            ))}
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-5 min-h-0">
          {mode === 'quick' ? (
            /* ── Quick Create ── */
            <div className="space-y-4">
              <div>
                <label className="text-[10px] font-semibold text-white/50 uppercase tracking-wider block mb-1.5">Event Name</label>
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-xl bg-white/[0.04] border border-white/[0.08] text-sm text-white placeholder:text-white/20 focus:outline-none focus:ring-1 focus:ring-primary/50 focus:border-primary/30"
                  placeholder="e.g. Summer Music Festival 2025"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] font-semibold text-white/50 uppercase tracking-wider block mb-1.5">Event Date</label>
                  <input
                    type="date"
                    value={eventDate}
                    onChange={(e) => setEventDate(e.target.value)}
                    className="w-full px-4 py-2.5 rounded-xl bg-white/[0.04] border border-white/[0.08] text-sm text-white focus:outline-none focus:ring-1 focus:ring-primary/50 focus:border-primary/30"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-semibold text-white/50 uppercase tracking-wider block mb-1.5">Event Start Time</label>
                  <input
                    type="time"
                    value={eventStartTime}
                    onChange={(e) => setEventStartTime(e.target.value)}
                    className="w-full px-4 py-2.5 rounded-xl bg-white/[0.04] border border-white/[0.08] text-sm text-white focus:outline-none focus:ring-1 focus:ring-primary/50 focus:border-primary/30"
                  />
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Map tab — keep mounted while switching tabs so Leaflet + drawings persist */}
              <div className={cn('space-y-3', activeTab !== 'map' && 'hidden')}>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                  <div className="flex-1">
                    <label className="text-[10px] font-semibold text-white/50 uppercase tracking-wider block mb-1.5">
                      Find by postcode
                    </label>
                    <div className="flex gap-2">
                      <input
                        value={postcodeSearch}
                        onChange={(e) => {
                          setPostcodeSearch(e.target.value);
                          if (postcodeError) setPostcodeError(null);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            void handlePostcodeSearch();
                          }
                        }}
                        className="flex-1 px-4 py-2.5 rounded-xl bg-white/[0.04] border border-white/[0.08] text-sm text-white placeholder:text-white/20 focus:outline-none focus:ring-1 focus:ring-primary/50 focus:border-primary/30 uppercase"
                        placeholder="e.g. SE21 7AR"
                        autoComplete="postal-code"
                      />
                      <button
                        type="button"
                        onClick={() => void handlePostcodeSearch()}
                        disabled={postcodeSearching || !postcodeSearch.trim()}
                        className={cn(
                          'inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-xs font-semibold border transition-all shrink-0',
                          postcodeSearching || !postcodeSearch.trim()
                            ? 'bg-white/[0.03] text-white/25 border-white/[0.06] cursor-not-allowed'
                            : 'bg-primary text-primary-foreground border-primary hover:bg-primary/90'
                        )}
                      >
                        {postcodeSearching ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Search className="w-3.5 h-3.5" />
                        )}
                        Find
                      </button>
                    </div>
                    {postcodeError && (
                      <p className="text-[11px] text-red-400 mt-1.5">{postcodeError}</p>
                    )}
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setPickingLocation((v) => !v)}
                    className={cn(
                      'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-semibold border transition-all',
                      pickingLocation
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-white/[0.04] text-white/70 border-white/[0.08] hover:bg-white/[0.06]'
                    )}
                  >
                    <MapPin className="w-3.5 h-3.5" />
                    {pickingLocation ? 'Click map to place pin…' : selectedLocation ? 'Move event pin' : 'Set event location'}
                  </button>
                  <p className="text-[11px] text-white/40">
                    Use the toolbar on the map for markers, routes, zones, and shapes.
                  </p>
                </div>

                <div
                  className={cn(
                    'p-3 rounded-xl border',
                    pickingLocation
                      ? 'bg-primary/10 border-primary/30'
                      : selectedLocation
                        ? 'bg-white/[0.02] border-white/[0.08]'
                        : 'bg-white/[0.02] border-white/[0.06]'
                  )}
                >
                  {pickingLocation ? (
                    <p className="text-xs text-primary font-semibold">
                      Pin mode — click once on the map to set the main event location, then use the draw tools freely.
                    </p>
                  ) : selectedLocation ? (
                    <div className="space-y-3">
                      <p className="text-xs text-white/70">
                        Event location:{' '}
                        <span className="font-mono text-primary">
                          {selectedLocation.lat.toFixed(5)}, {selectedLocation.lng.toFixed(5)}
                        </span>
                      </p>
                      <div>
                        <label className="text-[10px] font-semibold text-white/50 uppercase tracking-wider block mb-1.5">
                          Address label (optional)
                        </label>
                        <input
                          value={address}
                          onChange={(e) => setAddress(e.target.value)}
                          className="w-full px-4 py-2.5 rounded-xl bg-white/[0.04] border border-white/[0.08] text-sm text-white placeholder:text-white/20 focus:outline-none focus:ring-1 focus:ring-primary/50 focus:border-primary/30"
                          placeholder="e.g. Dulwich Cricket Club, main entrance"
                        />
                      </div>
                    </div>
                  ) : (
                    <p className="text-xs text-white/50">
                      Set the event pin with the button above, then plan the site with polygons, routes, and markers.
                    </p>
                  )}
                </div>

                <div className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-3">
                  <p className="text-[10px] font-semibold text-white/50 uppercase tracking-wider mb-2">
                    Site map overlay
                  </p>
                  <MapOverlayControls
                    overlay={mapOverlayDraft}
                    onChange={setMapOverlayDraft}
                    getMap={() => mapInstanceRef.current}
                    defaultCenter={
                      selectedLocation
                        ? [selectedLocation.lat, selectedLocation.lng]
                        : undefined
                    }
                  />
                </div>

                <div
                  className={cn(
                    'relative w-full h-[380px] rounded-xl overflow-hidden border border-white/[0.08]',
                    pickingLocation && 'cursor-crosshair'
                  )}
                >
                  <div ref={mapContainerRef} className="absolute inset-0 z-0 bg-[#1c2128]" />
                </div>
              </div>

              {activeTab === 'basics' ? (
            /* ── Event Basics Tab ── */
            <div className="space-y-4">
              <div className="p-4 rounded-xl border border-white/[0.06] bg-white/[0.02] space-y-4">
                <div>
                  <label className="text-[10px] font-semibold text-white/50 uppercase tracking-wider block mb-1.5">Event Name</label>
                  <input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="w-full px-4 py-2.5 rounded-xl bg-white/[0.04] border border-white/[0.08] text-sm text-white placeholder:text-white/20 focus:outline-none focus:ring-1 focus:ring-primary/50 focus:border-primary/30"
                    placeholder="e.g. Summer Music Festival 2025"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-[10px] font-semibold text-white/50 uppercase tracking-wider block mb-1.5">Event Date</label>
                    <input
                      type="date"
                      value={eventDate}
                      onChange={(e) => setEventDate(e.target.value)}
                      className="w-full px-4 py-2.5 rounded-xl bg-white/[0.04] border border-white/[0.08] text-sm text-white focus:outline-none focus:ring-1 focus:ring-primary/50 focus:border-primary/30"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold text-white/50 uppercase tracking-wider block mb-1.5">Event Start Time</label>
                    <input
                      type="time"
                      value={eventStartTime}
                      onChange={(e) => setEventStartTime(e.target.value)}
                      className="w-full px-4 py-2.5 rounded-xl bg-white/[0.04] border border-white/[0.08] text-sm text-white focus:outline-none focus:ring-1 focus:ring-primary/50 focus:border-primary/30"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="text-[10px] font-semibold text-white/50 uppercase tracking-wider block mb-1.5">Description</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  className="w-full px-4 py-2.5 rounded-xl bg-white/[0.04] border border-white/[0.08] text-sm text-white placeholder:text-white/20 focus:outline-none focus:ring-1 focus:ring-primary/50 focus:border-primary/30 resize-none"
                  placeholder="Describe the event..."
                />
              </div>

              <div>
                <label className="text-[10px] font-semibold text-white/50 uppercase tracking-wider block mb-1.5">Category</label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value as EventCategory)}
                  className="w-full px-4 py-2.5 rounded-xl bg-white/[0.04] border border-white/[0.08] text-sm text-white focus:outline-none focus:ring-1 focus:ring-primary/50 focus:border-primary/30"
                >
                  {categoryOptions.map((c) => (
                    <option key={c} value={c} className="bg-[#0f1526] text-white">
                      {c.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          ) : activeTab === 'resources' ? (
            /* ── Resources Tab ── */
            <div className="space-y-4">
              <p className="text-xs text-white/40">Add resources specific to this event. You can add more after the event is created.</p>

              {/* Add resource form */}
              <div className="p-4 rounded-xl border border-white/[0.06] bg-white/[0.02] space-y-3">
                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-1">
                    <label className="text-[10px] font-semibold text-white/50 uppercase tracking-wider block mb-1">Name</label>
                    <input
                      value={newResName}
                      onChange={(e) => setNewResName(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && addResource()}
                      className="w-full px-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08] text-xs text-white placeholder:text-white/20 focus:outline-none focus:ring-1 focus:ring-primary/50"
                      placeholder="e.g. Engine-7"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold text-white/50 uppercase tracking-wider block mb-1">Type</label>
                    <select
                      value={newResType}
                      onChange={(e) => setNewResType(e.target.value)}
                      className="w-full px-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08] text-xs text-white focus:outline-none focus:ring-1 focus:ring-primary/50"
                    >
                      {resourceTypes.map((t) => (
                        <option key={t} value={t} className="bg-[#0f1526] text-white">
                          {t.charAt(0).toUpperCase() + t.slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold text-white/50 uppercase tracking-wider block mb-1">Department</label>
                    <input
                      value={newResDept}
                      onChange={(e) => setNewResDept(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && addResource()}
                      className="w-full px-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08] text-xs text-white placeholder:text-white/20 focus:outline-none focus:ring-1 focus:ring-primary/50"
                      placeholder="e.g. Fire"
                    />
                  </div>
                </div>
                <button
                  onClick={addResource}
                  disabled={!newResName.trim()}
                  className={cn(
                    'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-semibold transition-all',
                    newResName.trim()
                      ? 'bg-primary/15 text-primary border border-primary/20 hover:bg-primary/25'
                      : 'bg-white/[0.03] text-white/20 border border-white/[0.04] cursor-not-allowed'
                  )}
                >
                  <Plus className="w-3 h-3" /> Add Resource
                </button>
              </div>

              {/* Resource list */}
              {eventResources.length === 0 ? (
                <div className="text-center py-6 text-white/20 text-xs">No resources added yet</div>
              ) : (
                <div className="space-y-1.5">
                  {eventResources.map((res) => (
                    <div
                      key={res.id}
                      className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.02] border border-white/[0.06]"
                    >
                      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Truck className="w-4 h-4 text-primary/70" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="text-xs font-bold text-white/80">{res.name}</span>
                        <span className="text-[10px] text-white/30 ml-2">{res.department} · {res.type}</span>
                      </div>
                      <button
                        onClick={() => removeResource(res.id)}
                        className="p-1 rounded hover:bg-red-500/10 transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5 text-red-400/60" />
                      </button>
                    </div>
                  ))}
                  <p className="text-[10px] text-primary font-semibold">
                    {eventResources.length} resource{eventResources.length !== 1 ? 's' : ''} added
                  </p>
                </div>
              )}
            </div>
          ) : activeTab === 'uploads' ? (
            /* ── Uploads Tab ── */
            <div className="space-y-4">
              <p className="text-xs text-white/40">
                Upload documents, plans, and files for this event. Available for printing in the workspace.
              </p>
              {/* Upload area */}
              <label className="flex flex-col items-center justify-center gap-2 p-8 rounded-xl border-2 border-dashed border-white/[0.1] bg-white/[0.02] cursor-pointer hover:bg-white/[0.04] hover:border-white/[0.15] transition-colors">
                <Upload className="w-8 h-8 text-white/20" />
                <span className="text-xs text-white/40 font-medium">Click to upload files</span>
                <span className="text-[10px] text-white/20">PDF, DOC, images, spreadsheets, etc.</span>
                <input
                  type="file"
                  multiple
                  onChange={handleFileUpload}
                  className="hidden"
                />
              </label>
              {/* File list */}
              {uploads.length > 0 && (
                <div className="space-y-1.5">
                  {uploads.map((file) => (
                    <div
                      key={file.id}
                      className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.03] border border-white/[0.06]"
                    >
                      <FileText className="w-4 h-4 text-primary/60 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-white/80 font-medium truncate">{file.name}</p>
                        <p className="text-[10px] text-white/30">{formatFileSize(file.size)}</p>
                      </div>
                      <button
                        onClick={() => removeUpload(file.id)}
                        className="p-1 rounded hover:bg-red-500/10 transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5 text-red-400/60" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : null}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center gap-3 px-6 py-4 border-t border-white/[0.06] flex-shrink-0">
          <button
            onClick={mode === 'quick' ? handleQuickCreate : handleCreate}
            disabled={creating || !title.trim() || (mode === 'detail' && !selectedLocation)}
            className={cn(
              'px-5 py-2.5 rounded-xl text-sm font-bold transition-all flex items-center gap-2',
              title.trim() && (mode === 'quick' || selectedLocation)
                ? 'bg-primary text-primary-foreground hover:bg-primary/90 active:scale-[0.98]'
                : 'bg-white/[0.04] text-white/20 cursor-not-allowed'
            )}
          >
            {creating && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {creating ? 'Creating...' : 'Create event'}
          </button>
          <button
            onClick={() => { reset(); onClose(); }}
            className="px-5 py-2.5 rounded-xl text-sm font-medium text-white/40 bg-white/[0.04] border border-white/[0.06] hover:bg-white/[0.06] transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
